<template>
  <div class="avatar-cropper">
    <div
      class="avatar-cropper-overlay"
      :class="{ 'avatar-cropper-overlay-inline': inline }"
      v-if="dataUrl"
    >
      <div class="avatar-cropper-mark" v-if="!inline">
        <a
          @click="cancel"
          class="avatar-cropper-close"
          :title="labels.cancel"
          href="javascript:;"
        >
          &times;
        </a>
      </div>
      <div class="avatar-cropper-container">
        <div class="avatar-cropper-image-container">
          <img
            ref="img"
            :src="dataUrl"
            alt
            @load.stop="createCropper"
            @error="onImgElementError"
          />
        </div>
        <slot></slot>
        <div class="avatar-cropper-footer">
          <button @click.stop.prevent="cancel" class="avatar-cropper-btn">
            {{ labels.cancel }}
          </button>

          <button @click.stop.prevent="submit" class="avatar-cropper-btn">
            {{ labels.submit }}
          </button>
        </div>
      </div>
    </div>
    <input
      v-if="!file"
      :accept="cleanedMimes"
      :capture="capture"
      class="avatar-cropper-img-input"
      ref="input"
      type="file"
      @change="onFileInputChange"
    />
  </div>
</template>

<script>
import Cropper from "cropperjs";
import mime from "mime-lite";
import { computed, defineComponent, onMounted, ref, watch } from "vue";

export default defineComponent({
  name: "AvatarCropper",

  emits: [
    "update:modelValue",
    "submit",
    "error",
    "cancel",
    "changed",
    "uploading",
    "completed",
    "uploaded",
  ],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },

    file: {
      type: File,
    },

    uploadHandler: {
      type: Function,
    },

    uploadUrl: {
      type: String,
    },

    requestOptions: {
      type: Object,
      default() {
        return {
          method: "POST",
        };
      },
    },

    uploadFileField: {
      type: String,
      default: "file",
    },

    uploadFileName: {
      type: [String, Function],
    },

    uploadFormData: {
      type: FormData,
      default() {
        return new FormData();
      },
    },

    cropperOptions: {
      type: Object,
      default() {
        return {
          aspectRatio: 1,
          autoCropArea: 1,
          viewMode: 1,
          movable: false,
          zoomable: false,
        };
      },
    },

    outputOptions: {
      type: Object,
    },

    outputMime: {
      type: String,
      default: null,
    },

    outputQuality: {
      type: Number,
      default: 0.9,
    },

    mimes: {
      type: String,
      default: "image/png, image/gif, image/jpeg, image/bmp, image/x-icon",
    },

    capture: {
      type: String,
    },

    labels: {
      type: Object,
      default() {
        return {
          submit: "Ok",
          cancel: "Cancel",
        };
      },
    },

    inline: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const cropper = ref(undefined);
    const dataUrl = ref(undefined);
    const fileName = ref(undefined);
    const mimeType = ref(undefined);
    const input = ref();
    const img = ref();

    const cleanedMimes = computed(() => {
      if (!props.mimes)
        throw new Error("vue-avatar-cropper: mimes prop cannot be empty");

      return props.mimes.trim().toLowerCase();
    });

    watch(
      () => props.modelValue,
      (value) => {
        if (!value) return;

        if (props.file) {
          onFileChange(props.file);
        } else {
          pickImage();
        }

        emit("update:modelValue", false);
      }
    );

    onMounted(() => {
      emit("update:modelValue", false);
    });

    function destroyCropper() {
      if (cropper.value) cropper.value.destroy();

      if (input.value) input.value.value = "";

      dataUrl.value = undefined;
    }

    function submit() {
      emit("submit");

      if (props.uploadUrl) {
        uploadImage();
      } else if (props.uploadHandler) {
        props.uploadHandler(cropper.value);
      } else {
        emit("error", {
          type: "user",
          message: "No upload handler found",
        });
      }

      destroyCropper();
    }

    function cancel() {
      emit("cancel");
      destroyCropper();
    }

    function onImgElementError() {
      emit("error", {
        type: "load",
        message: "File loading failed",
      });
      destroyCropper();
    }

    function pickImage() {
      if (input.value) input.value.click();
    }

    function onFileChange(file) {
      if (cleanedMimes.value === "image/*") {
        if (file.type.split("/")[0] !== "image") {
          emit("error", {
            type: "user",
            message: "File type not correct",
          });
          return;
        }
      } else if (cleanedMimes.value) {
        const correctType = cleanedMimes.value
          .split(", ")
          .find((mime) => mime === file.type);

        if (!correctType) {
          emit("error", {
            type: "user",
            message: "File type not correct",
          });
          return;
        }
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        dataUrl.value = e.target.result;
      };

      reader.readAsDataURL(file);

      fileName.value = file.name || "unknown";
      mimeType.value = file.type;

      emit("changed", {
        file,
        reader,
      });
    }

    function onFileInputChange(e) {
      if (!e.target.files || !e.target.files[0]) return;

      onFileChange(e.target.files[0]);
    }

    function createCropper() {
      cropper.value = new Cropper(img.value, props.cropperOptions);
    }

    function getFilename(blob) {
      const extension = mime.getExtension(blob.type);

      // Default logic
      if (!props.uploadFileName) {
        let actualFilename = fileName.value;

        const filenameParts = fileName.value.split(".");
        if (filenameParts.length > 1)
          actualFilename = filenameParts.slice(0, -1).join(".");

        return `${actualFilename}.${extension}`;
      }

      // User provided filename
      if (typeof props.uploadFileName === "string") return props.uploadFileName;

      if (typeof props.uploadFileName === "function")
        return props.uploadFileName({
          filename: fileName.value,
          mime: blob.type,
          extension,
        });

      return `unknown.${extension}`;
    }

    async function uploadImage() {
      cropper.value.getCroppedCanvas(props.outputOptions).toBlob(
        async (blob) => {
          const form = new FormData();

          for (const [key, value] of props.uploadFormData.entries()) {
            form.append(key, value);
          }

          form.append(props.uploadFileField, blob, getFilename(blob));

          const requestOptions = Object.assign(
            {
              body: form,
            },
            props.requestOptions
          );
          //
          const request = new Request(props.uploadUrl, requestOptions);

          const reqPromise = fetch(request);

          emit("uploading", {
            form,
            request,
            response: reqPromise,
          });

          try {
            const response = await reqPromise;
            emit("completed", {
              form,
              request,
              response,
            });

            if (response.ok) {
              emit("uploaded", {
                form,
                request,
                response,
              });
            }
          } catch (e) {
            emit("error", {
              type: "upload",
              message: "Image upload fail",
              // context: {
              //   request,
              //   response,
              // },
            });
          }
        },
        props.outputMime || mimeType.value,
        props.outputQuality
      );
    }

    return {
      cropper,
      dataUrl,
      fileName,
      mimeType,
      cleanedMimes,
      destroyCropper,
      cancel,
      input,
      img,
      createCropper,
      submit,
      onFileInputChange,
      onImgElementError,
    };
  },
});
</script>

<style lang="scss">

.cropper-view-box {
  display: block;
  height: 100%;
  outline: 1px solid $main-2 !important;
  outline-color: $main-2 !important;
  overflow: hidden;
  width: 100%;
}
.cropper-line {
  background-color: $main-2 !important;
}
.cropper-point {
  background-color: $main-2!important;
  height: 5px;
  opacity: 0.75;
  width: 5px;
}

.cropper-point.point-se::before {
  background-color: $main-2 !important;
  bottom: -50%;
  content: ' ';
  display: block;
  height: 200%;
  opacity: 0;
  position: absolute;
  right: -50%;
  width: 200%;
}

.avatar-cropper {
  
  .avatar-cropper-overlay {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99999;
  }

  .avatar-cropper-overlay-inline {
    position: initial;
  }

  .avatar-cropper-img-input {
    display: none;
  }

  .avatar-cropper-close {
    float: right;
    padding: 20px;
    font-size: 3rem;
    color: #fff;
    font-weight: 100;
    text-shadow: 0px 1px rgba(40, 40, 40, 0.3);
  }

  .avatar-cropper-mark {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
  }

  .avatar-cropper-container {
    background: #fff;
    z-index: 999;
    box-shadow: 1px 1px 5px rgba(100, 100, 100, 0.14);

    .avatar-cropper-image-container {
      position: relative;
      max-width: 600px;
      height: 600px;
    }

    img {
      height: 100%;
      width: 100%;
    }

    .avatar-cropper-footer {
      display: flex;
      align-items: stretch;
      align-content: stretch;
      justify-content: space-between;

      .avatar-cropper-btn {
        width: 50%;
        padding: 15px 0;
        cursor: pointer;
        border: none;
        background: transparent;
        outline: none;

        &:hover {
          background-color: $main-2 !important;
          color: #fff;
        }
      }
    }
  }
}
</style>
